import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getPreSignedURL } from '../api/action/IOT';
import { academyLogger } from "utils/logger";
import { cleanWordRichText } from '../utils/richText';

const extractS3Key = (src = '') => {
  if (!src || src.startsWith('data:') || src.startsWith('blob:')) return null;

  try {
    const parsedUrl = new URL(src);
    const host = parsedUrl.hostname.toLowerCase();
    if (!host.includes('s3') && !host.includes('amazonaws.com')) {
      return null;
    }
    return decodeURIComponent(parsedUrl.pathname.replace(/^\/+/, ''));
  } catch (error) {
    return src.startsWith('http') ? null : src;
  }
};

const toKebabCase = (value = '') => value.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);

const cssValue = (value) => {
  if (value === 'text.primary') return '#1f2937';
  if (typeof value === 'number') return String(value);
  return String(value);
};

const splitSx = (sx = {}) => {
  const baseStyle = {};
  const nested = {};

  Object.entries(sx || {}).forEach(([key, value]) => {
    if (key.startsWith('&')) {
      nested[key] = value;
    } else if (value !== undefined && value !== null && typeof value !== 'object') {
      baseStyle[key] = value === 'text.primary' ? '#1f2937' : value;
    }
  });

  return { baseStyle, nested };
};

const nestedSxToCss = (scopeClass, nested = {}) => (
  Object.entries(nested)
    .map(([selector, rules]) => {
      if (!rules || typeof rules !== 'object') return '';
      const body = Object.entries(rules)
        .filter(([, value]) => value !== undefined && value !== null && typeof value !== 'object')
        .map(([property, value]) => `${toKebabCase(property)}: ${cssValue(value)};`)
        .join(' ');
      if (!body) return '';
      return `.${scopeClass} ${selector.replace(/^&\s*/, '')} { ${body} }`;
    })
    .filter(Boolean)
    .join('\n')
);

const RichTextDisplay = ({ className = '', content, sx = {} }) => {
  const [processedContent, setProcessedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);
  const scopeClassRef = useRef(`rich-text-display-${Math.random().toString(36).slice(2)}`);
  const { baseStyle, nested } = useMemo(() => splitSx(sx), [sx]);
  const nestedCss = useMemo(() => nestedSxToCss(scopeClassRef.current, nested), [nested]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const processImages = async () => {
      if (!content) {
        setProcessedContent('');
        return;
      }

      setLoading(true);

      try {
        const cleanedContent = cleanWordRichText(content);
        const parser = new DOMParser();
        const doc = parser.parseFromString(cleanedContent, 'text/html');
        const images = doc.querySelectorAll('img');

        if (images.length === 0) {
          setProcessedContent(cleanedContent);
          setLoading(false);
          return;
        }

        await Promise.all(
          Array.from(images).map(async (img) => {
            const originalSrc = img.getAttribute('data-s3-key') || img.getAttribute('src');
            const s3Key = extractS3Key(originalSrc);

            if (!s3Key) {
              return;
            }

            try {
              const signedUrl = await getPreSignedURL(s3Key);
              if (signedUrl) {
                img.setAttribute('data-s3-key', s3Key);
                img.setAttribute('src', signedUrl);
              }
            } catch (error) {
              academyLogger.error('PreSigned URL generation failed:', error);
            }
          })
        );

        if (isMountedRef.current) {
          setProcessedContent(doc.body.innerHTML);
        }
      } catch (error) {
        academyLogger.error('Error processing rich text:', error);
        setProcessedContent(cleanWordRichText(content));
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    processImages();
  }, [content]);

  return (
    <div className="relative">
      <style>{`
        .rich-text-content {
          font-size: 16px;
          line-height: 1.6;
          font-family: Arial, sans-serif;
          color: #1f2937;
        }
        .rich-text-content h1 { font-size: 2em; font-weight: 700; margin: 16px 0 8px; }
        .rich-text-content h2 { font-size: 1.5em; font-weight: 700; margin: 14px 0 7px; }
        .rich-text-content h3 { font-size: 1.25em; font-weight: 700; margin: 12px 0 6px; }
        .rich-text-content h4 { font-size: 1.1em; font-weight: 700; margin: 10px 0 5px; }
        .rich-text-content p { margin: 8px 0; }
        .rich-text-content ul { margin: 8px 0; padding-left: 24px; list-style-type: disc; display: block; }
        .rich-text-content ol { margin: 8px 0; padding-left: 24px; list-style-type: decimal; display: block; }
        .rich-text-content li { margin: 4px 0; display: list-item; }
        .rich-text-content ul ul,
        .rich-text-content ol ul { list-style-type: circle; margin: 4px 0; }
        .rich-text-content ul ul ul,
        .rich-text-content ol ul ul,
        .rich-text-content ul ol ul,
        .rich-text-content ol ol ul { list-style-type: square; }
        .rich-text-content strong,
        .rich-text-content b { font-weight: 700; }
        .rich-text-content em,
        .rich-text-content i { font-style: italic; }
        .rich-text-content u { text-decoration: underline; }
        .rich-text-content strike { text-decoration: line-through; }
        .rich-text-content sub { vertical-align: sub; font-size: smaller; }
        .rich-text-content sup { vertical-align: super; font-size: smaller; }
        .rich-text-content blockquote {
          border-left: 4px solid #ccc;
          margin: 12px 0;
          padding-left: 16px;
          font-style: italic;
          color: #555;
        }
        .rich-text-content pre,
        .rich-text-content code {
          background-color: #f5f5f5;
          font-family: monospace;
          border-radius: 4px;
        }
        .rich-text-content pre {
          background: #0f172a;
          border: 1px solid #1e293b;
          color: #f8fafc;
          display: block;
          font-family: "Courier New", Consolas, Monaco, monospace;
          font-size: 13px;
          line-height: 1.65;
          margin: 12px 0;
          max-width: 100%;
          overflow-x: auto;
          padding: 14px 16px;
          tab-size: 2;
          white-space: pre-wrap;
        }
        .rich-text-content .ql-syntax {
          background: #0f172a;
          border: 1px solid #1e293b;
          border-radius: 8px;
          color: #f8fafc;
          display: block;
          font-family: "Courier New", Consolas, Monaco, monospace;
          font-size: 13px;
          line-height: 1.65;
          margin: 12px 0;
          max-width: 100%;
          overflow-x: auto;
          padding: 14px 16px;
          tab-size: 2;
          white-space: pre-wrap;
        }
        .rich-text-content pre *,
        .rich-text-content .ql-syntax * {
          color: inherit;
          font-family: inherit;
        }
        .rich-text-content code { padding: 2px 4px; }
        .rich-text-content pre code,
        .rich-text-content .ql-syntax code {
          background: transparent;
          color: inherit;
          padding: 0;
        }
        .rich-text-content a {
          color: #1976d2;
          text-decoration: underline;
        }
        .rich-text-content a:hover { color: #115293; }
        .rich-text-content img {
          width: 600px;
          height: 400px;
          object-fit: contain;
          display: block;
          margin: 10px auto;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          background-color: #f5f5f5;
          border: 2px solid #e0e0e0;
          max-width: 100%;
        }
        .rich-text-content .ql-align-center { text-align: center; }
        .rich-text-content .ql-align-right { text-align: right; }
        .rich-text-content .ql-align-justify { text-align: justify; }
        ${nestedCss}
      `}</style>
      {loading && (
        <div className="absolute inset-0 z-[1] flex items-center justify-center bg-white/60">
          <Loader2 className="h-7 w-7 animate-spin text-slate-600" aria-label="Loading content" />
        </div>
      )}

      <div
        className={`rich-text-content ${scopeClassRef.current} ${className}`.trim()}
        dangerouslySetInnerHTML={{ __html: processedContent }}
        style={baseStyle}
      />
    </div>
  );
};

export default RichTextDisplay;
